import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  ClipboardCheck, Plus, Eye, FileSpreadsheet, FileDown, AlertTriangle, Printer, Upload, Paperclip, X, Pencil, Wrench, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  qmsCheckService, qmsPlanService, clientService, productService, productRevisionService, attachmentService, productionLotService,
  qmsRqpService,
} from '../services/api';
import { useAuth } from '../context/AuthContext';

const ESITO_BADGE = {
  Conforme: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Non conforme': 'bg-red-100 text-red-800 border-red-300',
  'Conforme con note': 'bg-amber-100 text-amber-800 border-amber-300',
  'Conforme con deroga': 'bg-blue-100 text-blue-800 border-blue-300',
  'Incompleto': 'bg-slate-200 text-slate-700 border-slate-400',
  'In attesa decisione': 'bg-amber-100 text-amber-800 border-amber-300',
  'Richiesta modifica': 'bg-amber-100 text-amber-800 border-amber-300',
  'Lotto bloccato': 'bg-red-200 text-red-900 border-red-400',
  'Rilasciato con deroga': 'bg-blue-100 text-blue-800 border-blue-300',
  'Non applicabile': 'bg-slate-100 text-slate-600 border-slate-300',
};

const ALERT_DOT = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  grey: 'bg-slate-400',
};

export default function QmsChecksPage() {
  const { hasRole } = useAuth();
  const canCreate = hasRole('admin', 'quality', 'production', 'operator');

  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enums, setEnums] = useState(null);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [productRevisions, setProductRevisions] = useState([]);
  const [activeLots, setActiveLots] = useState([]);

  const [filters, setFilters] = useState({
    date_from: '', date_to: '', customer_code: 'all', product_code: '',
    finalita: 'all', esito: 'all', metodo: 'all', only_nc: false, only_benestare: false,
  });

  const [newOpen, setNewOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeCheck, setActiveCheck] = useState(null);
  const [activeAttachments, setActiveAttachments] = useState({});  // {id: {file_name, file_url, ...}}

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const buildFileUrl = (file_url) => {
    if (!file_url) return '#';
    if (file_url.startsWith('http')) return file_url;
    return `${BACKEND_URL}${file_url}`;
  };

  const initForm = () => ({
    data_ora_controllo: new Date().toISOString().slice(0, 16),
    controllore: '',
    customer_code: '', product_code: '', revision: '',
    commessa: '', lotto: '',
    fase_processo: 'OP10', finalita_controllo: 'In process',
    causale_controllo: 'In process',
    metodo_strumento: 'Strumento manuale',
    strumento_id: '', autorizzazione_strumento_scaduto: false,
    famiglia_strumento_generico: 'Calibro',
    descrizione_strumento_generico: '', codice_strumento_generico: '',
    esito_controllo: 'Conforme', note_controllo: '', azione_successiva: 'Nessuna',
    plan_id: '', plan_version: null, row_results: [],
    attachment_ids: [],
    _uploaded_global: [],  // [{id, file_name}]
    motivo_forzatura_esito_controllo: '',
  });

  const [esitoAutoControllo, setEsitoAutoControllo] = useState(null);
  const [editingCheckId, setEditingCheckId] = useState(null);  // null = create mode
  const [motivoModifica, setMotivoModifica] = useState('');

  // RQP creation dialog
  const [rqpOpen, setRqpOpen] = useState(false);
  const [rqpForm, setRqpForm] = useState({
    check_id: '', row_result_id: '',
    motivo_richiesta: '', attivita_richiesta: '', quantita_interessata: '',
    priorita: 'Normale', impatto_operativo: 'Quando possibile',
    richiede_ricontrollo_qualita: true, scadenza_richiesta: '', note_qualita: '',
  });
  const [rqpRowOptions, setRqpRowOptions] = useState([]);
  const [rqpEnums, setRqpEnums] = useState(null);

  useEffect(() => {
    qmsRqpService.getEnums().then(r => setRqpEnums(r.data)).catch(() => {});
  }, []);

  const openCreateRqp = (check) => {
    setRqpForm({
      check_id: check.id, row_result_id: '',
      motivo_richiesta: '', attivita_richiesta: '', quantita_interessata: '',
      priorita: 'Normale', impatto_operativo: 'Quando possibile',
      richiede_ricontrollo_qualita: true, scadenza_richiesta: '', note_qualita: '',
    });
    const rows = (check.row_results || []).map(r => ({
      id: r.id,
      label: `N° ${r.numero_caratteristica || r.snapshot_numero_caratteristica || '—'} · ${r.descrizione_caratteristica || r.snapshot_descrizione_caratteristica || ''} · ${r.risultato_misurato || r.risultato_testuale || '—'} (${r.esito_finale || r.esito_riga})`,
      esito: r.esito_finale || r.esito_riga,
    }));
    setRqpRowOptions(rows);
    setDetailOpen(false);
    setRqpOpen(true);
  };

  const handleRqpSubmit = async (e) => {
    e.preventDefault();
    if (!rqpForm.motivo_richiesta || !rqpForm.attivita_richiesta) {
      toast.error('Motivo e Attività richiesta sono obbligatori');
      return;
    }
    try {
      const payload = {
        check_id: rqpForm.check_id,
        row_result_id: rqpForm.row_result_id || null,
        motivo_richiesta: rqpForm.motivo_richiesta,
        attivita_richiesta: rqpForm.attivita_richiesta,
        quantita_interessata: rqpForm.quantita_interessata ? Number(rqpForm.quantita_interessata) : null,
        priorita: rqpForm.priorita,
        impatto_operativo: rqpForm.impatto_operativo,
        richiede_ricontrollo_qualita: rqpForm.richiede_ricontrollo_qualita,
        scadenza_richiesta: rqpForm.scadenza_richiesta || null,
        note_qualita: rqpForm.note_qualita || null,
      };
      const r = await qmsRqpService.createFromCheck(payload);
      toast.success(`Richiesta ${r.data.numero_richiesta} creata`);
      setRqpOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore creazione richiesta');
    }
  };
  const [form, setForm] = useState(initForm());

  // MIME validation map for tipo_allegato_richiesto
  const MIME_FOR_TYPE = {
    PDF: ['application/pdf'],
    CSV: ['text/csv', 'application/vnd.ms-excel'],
    TXT: ['text/plain'],
    QDAS: ['application/octet-stream', 'application/dfq'],
    Immagine: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
    Altro: [],
  };
  const ACCEPT_FOR_TYPE = {
    PDF: '.pdf', CSV: '.csv', TXT: '.txt', QDAS: '.dfq,.dfd,.dfx',
    Immagine: 'image/*', Altro: '',
  };

  const uploadFile = async (file, category = 'qms_check') => {
    const r = await attachmentService.upload(file, 'qms_check', 'pending', category, '');
    return { id: r.data.id, file_name: r.data.file_name };
  };

  const handleGlobalUpload = async (file) => {
    if (!file) return;
    try {
      const att = await uploadFile(file, 'qms_check');
      setForm(f => ({
        ...f,
        attachment_ids: [...f.attachment_ids, att.id],
        _uploaded_global: [...f._uploaded_global, att],
      }));
      toast.success(`Caricato ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upload');
    }
  };

  const handleRemoveGlobal = (attId) => {
    setForm(f => ({
      ...f,
      attachment_ids: f.attachment_ids.filter(id => id !== attId),
      _uploaded_global: f._uploaded_global.filter(a => a.id !== attId),
    }));
  };

  const handleRowUpload = async (rowIdx, file, requiredType) => {
    if (!file) return;
    // Frontend MIME validation when type is required
    if (requiredType && requiredType !== 'Altro') {
      const allowed = MIME_FOR_TYPE[requiredType] || [];
      if (allowed.length && !allowed.includes(file.type)) {
        toast.error(`Tipo file non valido. Richiesto: ${requiredType} (${allowed.join(', ')})`);
        return;
      }
    }
    try {
      const category = (form.row_results[rowIdx]?._pr?.tipo_caratteristica === 'Controllo 3D')
        ? 'check_3d' : 'check';
      const att = await uploadFile(file, category);
      setForm(f => ({
        ...f,
        row_results: f.row_results.map((r, i) => i === rowIdx ? {
          ...r,
          attachment_ids: [...(r.attachment_ids || []), att.id],
          _uploaded: [...(r._uploaded || []), att],
        } : r),
      }));
      toast.success(`Caricato ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upload riga');
    }
  };

  const handleRowRemove = (rowIdx, attId) => {
    setForm(f => ({
      ...f,
      row_results: f.row_results.map((r, i) => i === rowIdx ? {
        ...r,
        attachment_ids: (r.attachment_ids || []).filter(id => id !== attId),
        _uploaded: (r._uploaded || []).filter(a => a.id !== attId),
      } : r),
    }));
  };

  useEffect(() => {
    qmsCheckService.getEnums().then(r => setEnums(r.data));
    clientService.getAll().then(r => setClients(r.data || []));
    productService.getAll().then(r => setProducts(r.data || []));
    qmsCheckService.listCmmInstruments().then(r => setInstruments(r.data || []));
    load();
  }, []);
  useEffect(() => { load(); }, [filters]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.customer_code !== 'all') params.customer_code = filters.customer_code;
      if (filters.product_code) params.product_code = filters.product_code;
      if (filters.finalita !== 'all') params.finalita = filters.finalita;
      if (filters.esito !== 'all') params.esito = filters.esito;
      if (filters.metodo !== 'all') params.metodo = filters.metodo;
      if (filters.only_nc) params.only_nc = true;
      if (filters.only_benestare) params.only_benestare = true;
      const r = await qmsCheckService.list(params);
      setChecks(r.data || []);
    } finally { setLoading(false); }
  };

  // Load product revisions when product changes
  useEffect(() => {
    if (!form.product_code) { setProductRevisions([]); setActiveLots([]); return; }
    const product = products.find(p => p.code === form.product_code);
    if (!product) { setProductRevisions([]); setActiveLots([]); return; }
    productRevisionService.getAll({ product_id: product.id }).then(r => {
      const revs = r.data || [];
      setProductRevisions(revs);
      if (revs.length && !form.revision) {
        const active = revs.find(rev => rev.is_active) || revs[0];
        setForm(f => ({ ...f, revision: active.revision_code }));
      }
    }).catch(() => setProductRevisions([]));
    // Load active lots for this product (not closed/cancelled)
    productionLotService.getAll({ product_id: product.id }).then(r => {
      const lots = (r.data || []).filter(l => !['completed', 'cancelled', 'closed'].includes(l.status));
      setActiveLots(lots);
    }).catch(() => setActiveLots([]));
  }, [form.product_code, products, form.revision]);

  // Load applicable plan rows when customer+product+revision+fase+causale change
  useEffect(() => {
    const { customer_code, product_code, revision, fase_processo, causale_controllo } = form;
    if (!customer_code || !product_code || !revision || !fase_processo || !causale_controllo) return;
    qmsCheckService.getApplicableRows({
      customer_code, product_code, revision, fase_processo, causale_controllo,
    }).then(r => {
      const data = r.data;
      const planRows = data?.rows || [];
      setForm(f => ({
        ...f,
        plan_id: data?.plan?.id || '',
        plan_version: data?.plan?.versione_piano_controllo || null,
        row_results: planRows.map(pr => ({
          plan_row_id: pr.id,
          _pr: pr,
          risultato_misurato: '',
          risultato_testuale: '',
          esito_automatico: 'Manuale',
          esito_finale: '',
          motivo_forzatura_esito: '',
          _calc: null,  // last preview-conformity payload
          strumento_metodo_utilizzato: '',
          strumento_codificato_opzionale: '',
          strumento_generico_descrizione: '',
          note_riga: '',
          attachment_ids: [],
          _uploaded: [],
        })),
      }));
    }).catch(() => setForm(f => ({ ...f, plan_id: '', plan_version: null, row_results: [] })));
  }, [form.customer_code, form.product_code, form.revision, form.fase_processo, form.causale_controllo]);

  const selectedInstrument = useMemo(
    () => instruments.find(i => i.id === form.strumento_id),
    [form.strumento_id, instruments],
  );

  const handleNewSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      // Keep finalita and causale aligned
      payload.causale_controllo = form.causale_controllo || form.finalita_controllo;
      payload.finalita_controllo = form.finalita_controllo || form.causale_controllo;
      // Strip local UI fields from row_results
      payload.row_results = (form.row_results || []).map(r => ({
        id: r.id,  // preserved when editing
        plan_row_id: r.plan_row_id,
        risultato_misurato: r.risultato_misurato || null,
        risultato_testuale: r.risultato_testuale || null,
        esito_finale: r.esito_finale || null,
        motivo_forzatura_esito: r.motivo_forzatura_esito || null,
        strumento_metodo_utilizzato: r.strumento_metodo_utilizzato || null,
        strumento_codificato_opzionale: r.strumento_codificato_opzionale || null,
        strumento_generico_descrizione: r.strumento_generico_descrizione || null,
        note_riga: r.note_riga || null,
        attachment_ids: r.attachment_ids || [],
      }));
      if (!payload.plan_id) delete payload.plan_id;
      delete payload._uploaded_global;
      delete payload.plan_version;
      delete payload._lastAutoEsitoControllo;

      if (editingCheckId) {
        if (!motivoModifica.trim()) {
          toast.error('Motivo modifica obbligatorio per audit IATF');
          return;
        }
        payload.motivo_modifica = motivoModifica;
        await qmsCheckService.update(editingCheckId, payload);
        toast.success('Controllo aggiornato');
      } else {
        const r = await qmsCheckService.create(payload);
        if (r.data.esito_controllo === 'Non conforme' && r.data.plan_obbligatorie_nc > 0) {
          toast.warning(`Esito Non conforme: ${r.data.plan_obbligatorie_nc} caratteristiche obbligatorie NC`);
        } else {
          toast.success('Controllo registrato');
        }
      }
      setNewOpen(false);
      setForm(initForm());
      setEditingCheckId(null);
      setMotivoModifica('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore registrazione controllo');
    }
  };

  // Live preview conformity: debounced call per-row
  const previewTimers = useRef({});
  const triggerPreview = (idx, planRowId, risultato_misurato, risultato_testuale) => {
    if (previewTimers.current[idx]) clearTimeout(previewTimers.current[idx]);
    previewTimers.current[idx] = setTimeout(async () => {
      try {
        const r = await qmsCheckService.previewConformity({
          plan_row_id: planRowId,
          risultato_misurato: risultato_misurato || null,
          risultato_testuale: risultato_testuale || null,
        });
        setForm(f => ({
          ...f,
          row_results: f.row_results.map((x, i) => i === idx ? {
            ...x,
            _calc: r.data,
            esito_automatico: r.data.esito_automatico,
            // Auto-sync esito_finale to automatico unless user has explicitly overridden
            esito_finale: x.esito_finale && x.esito_finale !== x.esito_automatico ? x.esito_finale : r.data.esito_automatico,
          } : x),
        }));
      } catch (err) {
        // swallow preview errors
      }
    }, 350);
  };

  const updateRow = (idx, patch) => {
    setForm(f => ({
      ...f,
      row_results: f.row_results.map((x, i) => i === idx ? { ...x, ...patch } : x),
    }));
  };

  // Live overall esito preview: triggered after row changes settle
  const overallTimer = useRef(null);
  useEffect(() => {
    if (!form.plan_id || !form.row_results.length) {
      setEsitoAutoControllo(null);
      return;
    }
    if (overallTimer.current) clearTimeout(overallTimer.current);
    overallTimer.current = setTimeout(async () => {
      try {
        const r = await qmsCheckService.previewOverall({
          causale_controllo: form.causale_controllo,
          rows: form.row_results.map(rr => ({
            plan_row_id: rr.plan_row_id,
            esito_automatico: rr.esito_automatico,
            esito_finale: rr.esito_finale || rr.esito_automatico,
            risultato_misurato: rr.risultato_misurato || null,
            risultato_testuale: rr.risultato_testuale || null,
            attachment_ids: rr.attachment_ids || [],
          })),
        });
        const auto = r.data?.esito_automatico_controllo;
        setEsitoAutoControllo(auto);
        // Auto-sync esito_controllo to automatic unless user manually overrode it
        setForm(f => f.esito_controllo === f._lastAutoEsitoControllo || !f._lastAutoEsitoControllo
          ? { ...f, esito_controllo: auto, _lastAutoEsitoControllo: auto }
          : { ...f, _lastAutoEsitoControllo: auto });
      } catch {
        // swallow
      }
    }, 450);
  }, [form.row_results, form.plan_id, form.causale_controllo]);

  const openEditCheck = async (check) => {
    try {
      const r = await qmsCheckService.getById(check.id);
      const c = r.data;
      setForm({
        data_ora_controllo: c.data_ora_controllo ? c.data_ora_controllo.slice(0, 16) : new Date().toISOString().slice(0, 16),
        controllore: c.controllore || '',
        customer_code: c.customer_code || '',
        product_code: c.product_code || '',
        revision: c.revision || '',
        commessa: c.commessa || '',
        lotto: c.lotto || '',
        fase_processo: c.fase_processo || 'OP10',
        finalita_controllo: c.finalita_controllo || 'In process',
        causale_controllo: c.causale_controllo || c.finalita_controllo || 'In process',
        metodo_strumento: c.metodo_strumento || 'Strumento manuale',
        strumento_id: c.strumento_id || '',
        autorizzazione_strumento_scaduto: !!c.autorizzazione_strumento_scaduto,
        famiglia_strumento_generico: c.famiglia_strumento_generico || 'Calibro',
        descrizione_strumento_generico: c.descrizione_strumento_generico || '',
        codice_strumento_generico: c.codice_strumento_generico || '',
        esito_controllo: c.esito_controllo || 'Conforme',
        note_controllo: c.note_controllo || '',
        azione_successiva: c.azione_successiva || 'Nessuna',
        plan_id: c.plan_id || '',
        plan_version: null,
        row_results: (c.row_results || []).map(r => ({
          id: r.id,
          plan_row_id: r.plan_row_id,
          _pr: {
            id: r.plan_row_id,
            numero_caratteristica: r.numero_caratteristica || r.snapshot_numero_caratteristica,
            riferimento_pallinatura: r.snapshot_riferimento_pallinatura,
            descrizione_caratteristica: r.descrizione_caratteristica || r.snapshot_descrizione_caratteristica,
            tipo_caratteristica: r.tipo_caratteristica || r.snapshot_tipo_caratteristica,
            tipo_valutazione: r.snapshot_tipo_valutazione || 'Manuale',
            specifica_nominale: r.specifica_nominale || r.snapshot_specifica_nominale,
            tolleranza_min: r.tolleranza_min || r.snapshot_tolleranza_min,
            tolleranza_max: r.tolleranza_max || r.snapshot_tolleranza_max,
            limite_min_assoluto: r.snapshot_limite_min_assoluto,
            limite_max_assoluto: r.snapshot_limite_max_assoluto,
            unita_misura: r.unita_misura || r.snapshot_unita_misura,
            metodo_controllo: r.metodo_controllo_previsto || r.snapshot_metodo_controllo,
            note_operative: r.note_operative,
            obbligatorio: r.snapshot_obbligatoria ?? r.obbligatorio,
            richiede_allegato: r.snapshot_richiede_allegato ?? r.richiede_allegato,
            tipo_allegato_richiesto: r.snapshot_tipo_allegato_richiesto,
            applicabilita_corrente: {
              causale_controllo: r.snapshot_causale_controllo,
              attiva: true,
              obbligatoria: r.snapshot_obbligatoria,
              richiede_allegato: r.snapshot_richiede_allegato,
              tipo_allegato_richiesto: r.snapshot_tipo_allegato_richiesto,
              frequenza_controllo: r.snapshot_frequenza_controllo,
              quantita_campione: r.snapshot_quantita_campione,
            },
          },
          risultato_misurato: r.risultato_misurato || '',
          risultato_testuale: r.risultato_testuale || '',
          esito_automatico: r.esito_automatico || 'Manuale',
          esito_finale: r.esito_finale || r.esito_riga || '',
          motivo_forzatura_esito: r.motivo_forzatura_esito || '',
          _calc: {
            limite_inferiore_calcolato: r.snapshot_limite_inferiore_calcolato,
            limite_superiore_calcolato: r.snapshot_limite_superiore_calcolato,
            risultato_misurato_normalizzato: r.risultato_misurato_normalizzato,
            scostamento_dal_nominale: r.scostamento_dal_nominale,
            distanza_dal_limite_piu_vicino: r.distanza_dal_limite_piu_vicino,
            percentuale_utilizzo_tolleranza: r.percentuale_utilizzo_tolleranza,
          },
          strumento_metodo_utilizzato: r.strumento_metodo_utilizzato || '',
          strumento_codificato_opzionale: r.strumento_codificato_opzionale || '',
          strumento_generico_descrizione: r.strumento_generico_descrizione || '',
          note_riga: r.note_riga || '',
          attachment_ids: r.attachment_ids || [],
          _uploaded: [],
        })),
        attachment_ids: c.attachment_ids || [],
        _uploaded_global: [],
        motivo_forzatura_esito_controllo: c.motivo_forzatura_esito_controllo || '',
      });
      setEditingCheckId(check.id);
      setMotivoModifica('');
      setEsitoAutoControllo(c.esito_automatico_controllo);
      setDetailOpen(false);
      setNewOpen(true);
    } catch (err) {
      toast.error('Errore caricamento controllo per modifica');
    }
  };


  const openDetail = async (check) => {
    try {
      const r = await qmsCheckService.getById(check.id);
      const checkData = r.data;
      setActiveCheck(checkData);
      // Collect all attachment IDs (global + per row)
      const allIds = new Set([...(checkData.attachment_ids || [])]);
      (checkData.row_results || []).forEach(rr => {
        (rr.attachment_ids || []).forEach(id => allIds.add(id));
      });
      if (allIds.size > 0) {
        try {
          const attRes = await attachmentService.getByIds([...allIds]);
          const map = {};
          (attRes.data || []).forEach(a => { map[a.id] = a; });
          setActiveAttachments(map);
        } catch {
          setActiveAttachments({});
        }
      } else {
        setActiveAttachments({});
      }
      setDetailOpen(true);
    } catch {
      toast.error('Errore caricamento dettaglio');
    }
  };

  const downloadBlob = (data, filename, type) => {
    const blob = new Blob([data], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    const params = {};
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.customer_code !== 'all') params.customer_code = filters.customer_code;
    if (filters.product_code) params.product_code = filters.product_code;
    const r = await qmsCheckService.exportExcel(params);
    downloadBlob(r.data, `registro_controlli_${new Date().toISOString().slice(0, 10)}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  };
  const handleExportPdf = async () => {
    const params = {};
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.customer_code !== 'all') params.customer_code = filters.customer_code;
    if (filters.product_code) params.product_code = filters.product_code;
    const r = await qmsCheckService.exportPdf(params);
    downloadBlob(r.data, `registro_controlli_${new Date().toISOString().slice(0, 10)}.pdf`, 'application/pdf');
  };
  const handleDownloadSinglePdf = async (id) => {
    const r = await qmsCheckService.pdfSingle(id);
    downloadBlob(r.data, `controllo_${id}.pdf`, 'application/pdf');
  };

  if (!enums) return <div className="py-16 text-center"><div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto"></div></div>;

  return (
    <div className="space-y-6 pb-12" data-testid="qms-checks-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <ClipboardCheck className="w-8 h-8 text-slate-700" />
            Registro Controlli Qualità
          </h1>
          <p className="text-sm text-slate-500 mt-1">Controlli con snapshot piano, validazione tarature/allegati, workflow esiti.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={handleExportExcel} data-testid="export-excel-btn"><FileSpreadsheet className="w-3 h-3 mr-1" />Excel</Button>
          <Button size="sm" variant="outline" onClick={handleExportPdf} data-testid="export-pdf-btn"><FileDown className="w-3 h-3 mr-1" />PDF Registro</Button>
          {canCreate && <Button onClick={() => { setForm(initForm()); setNewOpen(true); }} data-testid="new-check-btn"><Plus className="w-4 h-4 mr-1" />Nuovo controllo</Button>}
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div><Label className="text-xs">Da</Label><Input type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-date-from" /></div>
            <div><Label className="text-xs">A</Label><Input type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-date-to" /></div>
            <div>
              <Label className="text-xs">Cliente</Label>
              <Select value={filters.customer_code} onValueChange={v => setFilters({ ...filters, customer_code: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.code}>{c.code} · {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Prodotto</Label><Input value={filters.product_code} onChange={e => setFilters({ ...filters, product_code: e.target.value })} placeholder="PROD001" /></div>
            <div>
              <Label className="text-xs">Esito</Label>
              <Select value={filters.esito} onValueChange={v => setFilters({ ...filters, esito: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {enums.esito_controllo.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Metodo</Label>
              <Select value={filters.metodo} onValueChange={v => setFilters({ ...filters, metodo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {enums.metodo_strumento.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-xs col-span-2 md:col-span-1">
              <Checkbox checked={filters.only_nc} onCheckedChange={v => setFilters({ ...filters, only_nc: !!v })} data-testid="filter-only-nc" />
              Solo NC
            </label>
            <label className="flex items-center gap-2 text-xs">
              <Checkbox checked={filters.only_benestare} onCheckedChange={v => setFilters({ ...filters, only_benestare: !!v })} />
              Solo benestare
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Controlli ({checks.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-center text-slate-500 py-8">Caricamento...</p> : checks.length === 0 ? <p className="text-center text-slate-500 py-8">Nessun controllo</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left uppercase tracking-wider text-slate-500 border-b">
                    <th className="py-2 pr-2">Data/Ora</th>
                    <th className="py-2 pr-2">Cliente</th>
                    <th className="py-2 pr-2">Prodotto/Rev</th>
                    <th className="py-2 pr-2">Fase</th>
                    <th className="py-2 pr-2">Finalità</th>
                    <th className="py-2 pr-2">Metodo / Strum.</th>
                    <th className="py-2 pr-2">Esito</th>
                    <th className="py-2 pr-2">Azione</th>
                    <th className="py-2 pr-2">Controllore</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map(c => (
                    <tr key={c.id} className="border-b hover:bg-slate-50 cursor-pointer" data-testid={`check-row-${c.id}`} onClick={() => openDetail(c)}>
                      <td className="py-2 pr-2 font-mono">{(c.data_ora_controllo || '').slice(0, 16).replace('T', ' ')}</td>
                      <td className="py-2 pr-2"><p className="font-mono">{c.customer_code}</p><p className="text-slate-500 truncate max-w-[140px]">{c.customer_name}</p></td>
                      <td className="py-2 pr-2 font-mono">{c.product_code}/{c.revision}</td>
                      <td className="py-2 pr-2">{c.fase_processo}</td>
                      <td className="py-2 pr-2">{c.finalita_controllo}</td>
                      <td className="py-2 pr-2 max-w-[180px] truncate">{c.metodo_strumento}{c.strumento_codice ? ` · ${c.strumento_codice}` : ''}</td>
                      <td className="py-2 pr-2">
                        <Badge className={`${ESITO_BADGE[c.esito_controllo] || ''} border`}>{c.esito_controllo}</Badge>
                        {c.plan_obbligatorie_nc > 0 && <Badge className="ml-1 bg-red-100 text-red-700 border border-red-300 text-[10px]">{c.plan_obbligatorie_nc} obbl NC</Badge>}
                      </td>
                      <td className="py-2 pr-2 max-w-[160px] truncate">{c.azione_successiva}</td>
                      <td className="py-2 pr-2 max-w-[160px] truncate">{c.controllore}</td>
                      <td className="py-2 pr-2"><Eye className="w-4 h-4 text-slate-400" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* NEW DIALOG */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCheckId ? '✏️ Modifica Controllo Qualità' : 'Nuovo Controllo Qualità'}</DialogTitle>
            <DialogDescription>
              {editingCheckId
                ? 'Modifica dei valori registrati. Lo snapshot delle specifiche (nominale/tolleranze/applicabilità) resta immutato per IATF. Ogni modifica è tracciata nell\'audit trail.'
                : 'Le righe del piano si caricano automaticamente per Cliente/Prodotto/Revisione/Fase.'}
            </DialogDescription>
          </DialogHeader>
          {editingCheckId && (
            <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded">
              <Label className="text-amber-800 text-xs uppercase tracking-wider font-bold">⚠️ Motivo modifica (obbligatorio per audit IATF)</Label>
              <Textarea
                rows={2}
                value={motivoModifica}
                onChange={e => setMotivoModifica(e.target.value)}
                placeholder="Es: correzione errore di digitazione, ripetizione misura..."
                className="bg-white mt-1"
                data-testid="motivo-modifica-input"
              />
            </div>
          )}
          <form onSubmit={handleNewSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Data/Ora</Label><Input type="datetime-local" value={form.data_ora_controllo} onChange={e => setForm({ ...form, data_ora_controllo: e.target.value })} /></div>
              <div>
                <Label>Cliente *</Label>
                <Select value={form.customer_code} onValueChange={v => setForm({ ...form, customer_code: v, product_code: '', revision: '' })}>
                  <SelectTrigger data-testid="new-customer"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.code}>{c.code} · {c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prodotto *</Label>
                <Select value={form.product_code} onValueChange={v => setForm({ ...form, product_code: v, revision: '' })}>
                  <SelectTrigger data-testid="new-product"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    {products
                      .filter(p => !form.customer_code || p.client_id === clients.find(c => c.code === form.customer_code)?.id)
                      .map(p => <SelectItem key={p.id} value={p.code}>{p.code} · {p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Revisione *</Label>
                <Select value={form.revision} onValueChange={v => setForm({ ...form, revision: v })}>
                  <SelectTrigger data-testid="new-revision"><SelectValue placeholder={form.product_code ? 'Seleziona' : 'Scegli prodotto'} /></SelectTrigger>
                  <SelectContent>
                    {productRevisions.length === 0 && <SelectItem value="__empty" disabled>Nessuna revisione</SelectItem>}
                    {productRevisions.map(r => <SelectItem key={r.id} value={r.revision_code}>{r.revision_code}{r.is_active ? ' · attiva' : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Commessa</Label><Input value={form.commessa} onChange={e => setForm({ ...form, commessa: e.target.value })} /></div>
              <div>
                <Label>Lotto produzione</Label>
                <Select
                  value={form.lotto || '__none'}
                  onValueChange={v => setForm({ ...form, lotto: v === '__none' ? '' : v })}
                >
                  <SelectTrigger data-testid="new-lotto"><SelectValue placeholder={form.product_code ? 'Seleziona' : 'Scegli prodotto'} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— Nessun lotto —</SelectItem>
                    {activeLots.length === 0 && form.product_code && <SelectItem value="__empty" disabled>Nessun lotto attivo per questo prodotto</SelectItem>}
                    {activeLots.map(l => (
                      <SelectItem key={l.id} value={l.lot_code}>
                        {l.lot_code} · {l.quantity_planned} pz · {l.status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fase processo *</Label>
                <Select value={form.fase_processo} onValueChange={v => setForm({ ...form, fase_processo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{enums.fase_processo.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Causale *</Label>
                <Select value={form.causale_controllo} onValueChange={v => setForm({ ...form, causale_controllo: v, finalita_controllo: v })}>
                  <SelectTrigger data-testid="new-causale"><SelectValue /></SelectTrigger>
                  <SelectContent>{enums.causale_controllo.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Metodo *</Label>
                <Select value={form.metodo_strumento} onValueChange={v => setForm({ ...form, metodo_strumento: v, strumento_id: '' })}>
                  <SelectTrigger data-testid="new-metodo"><SelectValue /></SelectTrigger>
                  <SelectContent>{enums.metodo_strumento.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {form.metodo_strumento === 'CMM / macchina di misura' && (
              <Card className="bg-slate-50">
                <CardContent className="p-3 space-y-2">
                  <Label>Strumento codificato *</Label>
                  <Select value={form.strumento_id} onValueChange={v => setForm({ ...form, strumento_id: v })}>
                    <SelectTrigger data-testid="new-instrument"><SelectValue placeholder="Seleziona strumento" /></SelectTrigger>
                    <SelectContent>{instruments.map(i => <SelectItem key={i.id} value={i.id}>{i.code} · {i.description}</SelectItem>)}</SelectContent>
                  </Select>
                  {selectedInstrument && (
                    <div className="flex items-center gap-3 text-xs">
                      <span className={`w-2 h-2 rounded-full ${ALERT_DOT[selectedInstrument.alert_color]}`}></span>
                      <span>Scadenza taratura: <b>{selectedInstrument.next_calibration_date || '—'}</b></span>
                      <span className="text-slate-500">Ultima: {selectedInstrument.last_calibration_date || '—'}</span>
                    </div>
                  )}
                  {selectedInstrument?.alert_color === 'red' && (
                    <label className="flex items-center gap-2 text-sm text-red-700">
                      <Checkbox checked={form.autorizzazione_strumento_scaduto} onCheckedChange={v => setForm({ ...form, autorizzazione_strumento_scaduto: !!v })} data-testid="new-auth-expired" />
                      Autorizzo l'uso dello strumento con taratura scaduta
                    </label>
                  )}
                </CardContent>
              </Card>
            )}

            {form.metodo_strumento === 'Strumento manuale' && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Famiglia</Label>
                  <Select value={form.famiglia_strumento_generico} onValueChange={v => setForm({ ...form, famiglia_strumento_generico: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{enums.famiglia_strumento_generico.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Descrizione</Label><Input value={form.descrizione_strumento_generico} onChange={e => setForm({ ...form, descrizione_strumento_generico: e.target.value })} /></div>
                <div className="col-span-3"><Label>Codice (opz.)</Label><Input value={form.codice_strumento_generico} onChange={e => setForm({ ...form, codice_strumento_generico: e.target.value })} /></div>
              </div>
            )}

            {/* Esito automatico live preview */}
            {esitoAutoControllo && form.row_results.length > 0 && (
              <div className={`p-3 rounded border-l-4 ${
                esitoAutoControllo === 'Conforme' ? 'bg-emerald-50 border-l-emerald-500 text-emerald-900' :
                esitoAutoControllo === 'Non conforme' ? 'bg-red-50 border-l-red-500 text-red-900' :
                esitoAutoControllo === 'Incompleto' ? 'bg-slate-100 border-l-slate-500 text-slate-800' :
                esitoAutoControllo === 'In attesa decisione' ? 'bg-amber-50 border-l-amber-500 text-amber-900' :
                esitoAutoControllo === 'Conforme con deroga' ? 'bg-blue-50 border-l-blue-500 text-blue-900' :
                'bg-amber-50 border-l-amber-400 text-amber-900'
              }`}>
                <p className="text-xs uppercase font-bold tracking-wider opacity-70">Esito automatico calcolato</p>
                <p className="text-lg font-bold" data-testid="esito-auto-controllo">{esitoAutoControllo}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Esito finale controllo *</Label>
                <Select value={form.esito_controllo} onValueChange={v => setForm({ ...form, esito_controllo: v })}>
                  <SelectTrigger data-testid="new-esito"><SelectValue /></SelectTrigger>
                  <SelectContent>{enums.esito_controllo.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
                {esitoAutoControllo && form.esito_controllo !== esitoAutoControllo && (
                  <p className="text-[10px] text-amber-700 mt-1">⚠️ Esito finale diverso da automatico ({esitoAutoControllo}). Motivazione obbligatoria.</p>
                )}
              </div>
              <div>
                <Label>Azione successiva</Label>
                <Select value={form.azione_successiva} onValueChange={v => setForm({ ...form, azione_successiva: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{enums.azione_successiva.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {esitoAutoControllo && form.esito_controllo !== esitoAutoControllo && (
              <div>
                <Label className="text-amber-700">⚠️ Motivo forzatura esito complessivo *</Label>
                <Textarea
                  rows={2}
                  value={form.motivo_forzatura_esito_controllo}
                  onChange={e => setForm({ ...form, motivo_forzatura_esito_controllo: e.target.value })}
                  placeholder="Es: Rilascio approvato dal cliente con deroga DDA-2026-001"
                  className="border-amber-400 bg-amber-50"
                  data-testid="motivo-forzatura-controllo"
                />
              </div>
            )}

            <div className="rounded-lg border-2 border-blue-100 bg-blue-50/30 p-3 space-y-1.5" data-testid="note-controllo-section">
              <Label className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                <FileText className="w-4 h-4" />Note controllo
              </Label>
              <Textarea
                rows={5}
                value={form.note_controllo}
                onChange={e => setForm({ ...form, note_controllo: e.target.value })}
                placeholder="Es: identifica un pezzo specifico, dettagli aggiuntivi di ispezione, osservazioni per la produzione o cliente…"
                className="bg-white"
                data-testid="note-controllo-input"
              />
              <p className="text-[10px] text-slate-500">
                Il testo sarà visibile in dettaglio controllo, report stampato e archivio storico.
              </p>
            </div>

            {/* Global attachments */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2"><Paperclip className="w-4 h-4" />Allegati controllo (opzionali)</Label>
                <label className="cursor-pointer">
                  <input type="file" hidden onChange={e => { handleGlobalUpload(e.target.files?.[0]); e.target.value = ''; }} data-testid="global-upload-input" />
                  <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium border rounded hover:bg-slate-50 cursor-pointer">
                    <Upload className="w-3 h-3" />Carica file
                  </span>
                </label>
              </div>
              {form._uploaded_global.length > 0 && (
                <div className="space-y-1">
                  {form._uploaded_global.map(att => (
                    <div key={att.id} className="flex items-center justify-between text-xs bg-slate-50 px-2 py-1 rounded">
                      <span className="flex items-center gap-1"><Paperclip className="w-3 h-3 text-slate-500" />{att.file_name}</span>
                      <button type="button" onClick={() => handleRemoveGlobal(att.id)} className="text-red-500 hover:text-red-700"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Piano rows */}
            {form.plan_id ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-slate-700">Quote applicabili — Fase {form.fase_processo} · Causale {form.causale_controllo}</h3>
                  <Badge className="bg-slate-100 text-slate-700 border border-slate-300">{form.row_results.length} quote</Badge>
                  {form.plan_version && <Badge className="bg-blue-50 text-blue-700 border border-blue-200">Piano v{form.plan_version}</Badge>}
                </div>
                {form.row_results.length === 0 && <p className="text-sm text-slate-500 italic">Nessuna quota applicabile per {form.fase_processo} · {form.causale_controllo}.</p>}
                {form.row_results.map((rr, idx) => {
                  const pr = rr._pr;
                  const calc = rr._calc;
                  const tipoVal = pr.tipo_valutazione || 'Manuale';
                  const isNumericType = ['Numerica con nominale e tolleranze', 'Limite massimo', 'Limite minimo', 'Range min/max assoluto'].includes(tipoVal);
                  const isOkKo = tipoVal === 'OK / KO';
                  const isPresenzaAssenza = tipoVal === 'Presenza / Assenza';
                  const isSoloAllegato = tipoVal === 'Solo allegato';
                  const applic = pr.applicabilita_corrente || {};
                  const richiedeAllegato = applic.richiede_allegato ?? pr.richiede_allegato;
                  const tipoAllegato = applic.tipo_allegato_richiesto ?? pr.tipo_allegato_richiesto;
                  const obbligatoria = applic.obbligatoria ?? pr.obbligatorio;
                  const um = pr.unita_misura || '';

                  // Color based on esito
                  const esito = rr.esito_finale || rr.esito_automatico;
                  const borderColor = esito === 'Conforme' ? 'border-l-emerald-500' :
                    esito === 'Non conforme' ? 'border-l-red-500' :
                    esito === 'In attesa decisione' ? 'border-l-amber-500' :
                    esito === 'Conforme con note' ? 'border-l-amber-400' :
                    'border-l-slate-400';

                  const fmt = (v) => v == null ? '—' : (typeof v === 'number' ? v.toLocaleString('it-IT', { maximumFractionDigits: 6 }) : v);

                  return (
                    <Card key={rr.plan_row_id} className={`border-l-4 ${borderColor}`}>
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold text-sm">N° {pr.numero_caratteristica}</span>
                          {pr.riferimento_pallinatura && <Badge className="bg-purple-100 text-purple-800 border border-purple-300 text-[10px]">Pall. {pr.riferimento_pallinatura}</Badge>}
                          <Badge className="bg-blue-100 text-blue-800 border border-blue-300 text-[10px]">{pr.tipo_caratteristica}</Badge>
                          <Badge className="bg-slate-100 text-slate-700 border border-slate-300 text-[10px]">{tipoVal}</Badge>
                          {obbligatoria && <Badge className="bg-red-100 text-red-700 border border-red-300 text-[10px]">OBBL</Badge>}
                          {richiedeAllegato && <Badge className="bg-amber-100 text-amber-700 border border-amber-300 text-[10px]">ALL {tipoAllegato || ''}</Badge>}
                          {applic.frequenza_controllo && <Badge className="bg-slate-100 text-slate-600 border text-[10px]">Freq: {applic.frequenza_controllo}</Badge>}
                          {applic.quantita_campione != null && <Badge className="bg-slate-100 text-slate-600 border text-[10px]">Qty camp: {applic.quantita_campione}</Badge>}
                        </div>
                        <p className="text-sm font-medium">{pr.descrizione_caratteristica}</p>
                        <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded font-mono">
                          {isNumericType && tipoVal === 'Numerica con nominale e tolleranze' && (
                            <>Nom: {pr.specifica_nominale || '—'} · Tol: {pr.tolleranza_min || '—'} / {pr.tolleranza_max || '—'} {um} · </>
                          )}
                          {(tipoVal === 'Limite massimo' || tipoVal === 'Range min/max assoluto') && (
                            <>Lim. max: {pr.limite_max_assoluto || '—'} {um} · </>
                          )}
                          {(tipoVal === 'Limite minimo' || tipoVal === 'Range min/max assoluto') && (
                            <>Lim. min: {pr.limite_min_assoluto || '—'} {um} · </>
                          )}
                          Metodo: {pr.metodo_controllo} · Strum: {pr.strumento_consigliato || '—'}
                        </p>
                        {pr.note_operative && <p className="text-xs italic text-slate-500">{pr.note_operative}</p>}
                        {pr.note_valutazione && <p className="text-xs italic text-blue-700">📐 {pr.note_valutazione}</p>}

                        <div className="grid grid-cols-12 gap-2">
                          {/* Input value (varia per tipo) */}
                          {isNumericType && (
                            <Input
                              className="col-span-3 font-mono"
                              placeholder={`Valore ${um || ''} (virgola o punto)`}
                              value={rr.risultato_misurato}
                              onChange={e => {
                                const v = e.target.value;
                                updateRow(idx, { risultato_misurato: v });
                                triggerPreview(idx, rr.plan_row_id, v, null);
                              }}
                              data-testid={`row-result-${idx}`}
                            />
                          )}
                          {isOkKo && (
                            <div className="col-span-3">
                              <Select value={rr.risultato_testuale || ''} onValueChange={v => {
                                updateRow(idx, { risultato_testuale: v });
                                triggerPreview(idx, rr.plan_row_id, null, v);
                              }}>
                                <SelectTrigger data-testid={`row-okko-${idx}`}><SelectValue placeholder="OK / KO" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="OK">OK</SelectItem>
                                  <SelectItem value="KO">KO</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          {isPresenzaAssenza && (
                            <div className="col-span-3">
                              <Select value={rr.risultato_testuale || ''} onValueChange={v => {
                                updateRow(idx, { risultato_testuale: v });
                                triggerPreview(idx, rr.plan_row_id, null, v);
                              }}>
                                <SelectTrigger data-testid={`row-pa-${idx}`}><SelectValue placeholder="Presente / Assente" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Presente">Presente</SelectItem>
                                  <SelectItem value="Assente">Assente</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          {(isSoloAllegato || tipoVal === 'Manuale') && (
                            <Input
                              className="col-span-3"
                              placeholder={isSoloAllegato ? 'Solo allegato richiesto' : 'Note esito'}
                              value={rr.risultato_misurato}
                              onChange={e => updateRow(idx, { risultato_misurato: e.target.value })}
                              data-testid={`row-result-${idx}`}
                            />
                          )}

                          {/* Esito automatico (readonly) */}
                          <div className="col-span-2">
                            <Badge className={`w-full justify-center py-1.5 ${
                              rr.esito_automatico === 'Conforme' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                              rr.esito_automatico === 'Non conforme' ? 'bg-red-100 text-red-800 border border-red-300' :
                              'bg-slate-100 text-slate-600 border border-slate-300'
                            }`} data-testid={`row-auto-${idx}`}>
                              {rr.esito_automatico === 'Conforme' ? '✓ Conforme' :
                                rr.esito_automatico === 'Non conforme' ? '✗ NC (auto)' :
                                'Manuale'}
                            </Badge>
                          </div>

                          {/* Esito finale (può essere modificato) */}
                          <div className="col-span-3">
                            <Select value={rr.esito_finale || rr.esito_automatico} onValueChange={v => updateRow(idx, { esito_finale: v })}>
                              <SelectTrigger data-testid={`row-esito-finale-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent>{enums.esito_riga.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>

                          <Input className="col-span-4" placeholder="Strumento utilizzato (opz.)"
                            value={rr.strumento_metodo_utilizzato || ''}
                            onChange={e => updateRow(idx, { strumento_metodo_utilizzato: e.target.value })}
                          />

                          {/* Calc summary */}
                          {calc && (calc.limite_inferiore_calcolato != null || calc.limite_superiore_calcolato != null || calc.scostamento_dal_nominale != null) && (
                            <div className="col-span-12 bg-slate-50 border border-slate-200 rounded p-2 text-[11px] font-mono space-y-0.5">
                              {(calc.limite_inferiore_calcolato != null || calc.limite_superiore_calcolato != null) && (
                                <p>Limiti calcolati: <b>{fmt(calc.limite_inferiore_calcolato)}</b> ÷ <b>{fmt(calc.limite_superiore_calcolato)}</b> {um}</p>
                              )}
                              {calc.risultato_misurato_normalizzato != null && (
                                <p>Valore: <b>{fmt(calc.risultato_misurato_normalizzato)}</b> {um}
                                  {calc.scostamento_dal_nominale != null && <> · Scostamento nominale: <b>{calc.scostamento_dal_nominale >= 0 ? '+' : ''}{fmt(calc.scostamento_dal_nominale)}</b> {um}</>}
                                  {calc.distanza_dal_limite_piu_vicino != null && <> · Dist. limite: <b>{fmt(calc.distanza_dal_limite_piu_vicino)}</b> {um}</>}
                                </p>
                              )}
                              {calc.percentuale_utilizzo_tolleranza != null && (
                                <p>Uso tolleranza: <b>{calc.percentuale_utilizzo_tolleranza}%</b></p>
                              )}
                            </div>
                          )}

                          {/* Forzatura motivazione (solo se differente) */}
                          {rr.esito_finale && rr.esito_finale !== rr.esito_automatico && rr.esito_automatico !== 'Manuale' && (
                            <Input className="col-span-12 border-amber-400 bg-amber-50" placeholder="⚠️ Motivo forzatura esito (obbligatorio)"
                              value={rr.motivo_forzatura_esito || ''}
                              onChange={e => updateRow(idx, { motivo_forzatura_esito: e.target.value })}
                              data-testid={`row-motivo-${idx}`}
                            />
                          )}

                          <Input className="col-span-12" placeholder="Note riga"
                            value={rr.note_riga || ''}
                            onChange={e => updateRow(idx, { note_riga: e.target.value })}
                          />

                          <div className="col-span-12 flex items-center gap-2 flex-wrap">
                            <label className="cursor-pointer">
                              <input type="file"
                                hidden
                                accept={ACCEPT_FOR_TYPE[tipoAllegato] || ''}
                                onChange={e => { handleRowUpload(idx, e.target.files?.[0], tipoAllegato); e.target.value = ''; }}
                                data-testid={`row-upload-${idx}`}
                              />
                              <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border rounded hover:bg-slate-50 cursor-pointer ${richiedeAllegato && (rr.attachment_ids || []).length === 0 ? 'border-red-400 text-red-600' : ''}`}>
                                <Upload className="w-3 h-3" />
                                {richiedeAllegato ? `Allegato OBBL${tipoAllegato ? ` (${tipoAllegato})` : ''}` : 'Allegato opz.'}
                              </span>
                            </label>
                            {(rr._uploaded || []).map(att => (
                              <span key={att.id} className="inline-flex items-center gap-1 text-[10px] bg-slate-100 px-2 py-1 rounded">
                                <Paperclip className="w-3 h-3" />{att.file_name}
                                <button type="button" onClick={() => handleRowRemove(idx, att.id)} className="text-red-500"><X className="w-3 h-3" /></button>
                              </span>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : form.customer_code && form.product_code && form.revision && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
                Nessun piano controlli attivo per {form.customer_code}/{form.product_code} rev. {form.revision}, oppure nessuna quota applicabile per {form.fase_processo} · {form.causale_controllo}. Il controllo sarà registrato come libero.
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setNewOpen(false); setEditingCheckId(null); setMotivoModifica(''); }}>Annulla</Button>
              <Button type="submit" data-testid="new-submit">{editingCheckId ? 'Salva modifiche' : 'Registra Controllo'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* DETAIL DIALOG */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettaglio Controllo</DialogTitle>
          </DialogHeader>
          {activeCheck && (
            <div className="space-y-4">
              <Card>
                <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-slate-500">Data/Ora</span><p className="font-mono">{(activeCheck.data_ora_controllo || '').slice(0, 19).replace('T', ' ')}</p></div>
                  <div><span className="text-slate-500">Controllore</span><p>{activeCheck.controllore || '—'}</p></div>
                  <div><span className="text-slate-500">Cliente</span><p className="font-mono">{activeCheck.customer_code}</p><p className="text-xs text-slate-500">{activeCheck.customer_name}</p></div>
                  <div><span className="text-slate-500">Prodotto/Rev</span><p className="font-mono">{activeCheck.product_code} / {activeCheck.revision}</p><p className="text-xs text-slate-500">{activeCheck.descrizione_prodotto}</p></div>
                  <div><span className="text-slate-500">Disegno</span><p className="font-mono">{activeCheck.codice_disegno || '—'}</p></div>
                  <div><span className="text-slate-500">ID Config</span><p className="font-mono">{activeCheck.id_configurazione || '—'}</p></div>
                  <div><span className="text-slate-500">Commessa</span><p>{activeCheck.commessa || '—'}</p></div>
                  <div><span className="text-slate-500">Lotto</span><p>{activeCheck.lotto || '—'}</p></div>
                  <div><span className="text-slate-500">Fase</span><p>{activeCheck.fase_processo}</p></div>
                  <div><span className="text-slate-500">Finalità</span><p>{activeCheck.finalita_controllo}</p></div>
                  <div className="col-span-2">
                    <span className="text-slate-500">Esito complessivo</span>
                    <div className="flex items-center gap-2 flex-wrap"><Badge className={`${ESITO_BADGE[activeCheck.esito_controllo]} border`}>{activeCheck.esito_controllo}</Badge>
                      {activeCheck.esito_automatico_controllo && activeCheck.esito_automatico_controllo !== activeCheck.esito_controllo && (
                        <Badge className="bg-slate-100 text-slate-600 border border-slate-300 text-[10px]">Auto: {activeCheck.esito_automatico_controllo}</Badge>
                      )}
                      {activeCheck.plan_obbligatorie_nc > 0 && <Badge className="bg-red-100 text-red-700 border border-red-300"><AlertTriangle className="w-3 h-3 mr-1 inline" />{activeCheck.plan_obbligatorie_nc} obbl NC</Badge>}
                    </div>
                    {activeCheck.forzato_da_controllo && (
                      <div className="mt-2 p-2 bg-amber-50 border-l-2 border-amber-400 rounded text-xs">
                        <p className="text-amber-900"><b>⚠️ Esito forzato</b> da <span className="font-mono">{activeCheck.forzato_da_controllo}</span> il {activeCheck.data_forzatura_controllo ? new Date(activeCheck.data_forzatura_controllo).toLocaleString('it-IT') : '—'}</p>
                        {activeCheck.motivo_forzatura_esito_controllo && <p className="text-amber-800 italic mt-1">&laquo;{activeCheck.motivo_forzatura_esito_controllo}&raquo;</p>}
                      </div>
                    )}
                    {activeCheck.ultima_modifica_da && (
                      <div className="mt-2 p-2 bg-blue-50 border-l-2 border-blue-400 rounded text-xs" data-testid="check-edit-info">
                        <p className="text-blue-900"><b>✏️ Modificato</b> da <span className="font-mono">{activeCheck.ultima_modifica_da}</span> il {activeCheck.data_ultima_modifica ? new Date(activeCheck.data_ultima_modifica).toLocaleString('it-IT') : '—'}</p>
                        {activeCheck.motivo_ultima_modifica && <p className="text-blue-800 italic mt-1">&laquo;{activeCheck.motivo_ultima_modifica}&raquo;</p>}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Metodo e Strumento</CardTitle></CardHeader>
                <CardContent className="p-4 text-sm space-y-2">
                  <p><b>Metodo:</b> {activeCheck.metodo_strumento}</p>
                  {activeCheck.strumento_codice && (
                    <div>
                      <p><b>Strumento:</b> <span className="font-mono">{activeCheck.strumento_codice}</span> — {activeCheck.strumento_descrizione}</p>
                      <p className="text-xs flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${ALERT_DOT[activeCheck.alert_color_strumento]}`}></span>
                        Stato: {activeCheck.stato_taratura_strumento || '—'} · Ultima {activeCheck.data_ultima_taratura_strumento || '—'} · Prossima {activeCheck.data_prossima_taratura_strumento || '—'}
                      </p>
                      {activeCheck.autorizzazione_strumento_scaduto && <Badge className="bg-amber-100 text-amber-800 border border-amber-300">Autorizzazione strumento scaduto</Badge>}
                    </div>
                  )}
                  {activeCheck.famiglia_strumento_generico && (
                    <p><b>Strum. generico:</b> {activeCheck.famiglia_strumento_generico} · {activeCheck.descrizione_strumento_generico} {activeCheck.codice_strumento_generico ? `· ${activeCheck.codice_strumento_generico}` : ''}</p>
                  )}
                  <p><b>Azione successiva:</b> {activeCheck.azione_successiva}</p>
                </CardContent>
              </Card>

              {/* Note controllo - sezione dedicata */}
              {activeCheck.note_controllo && (
                <Card className="border-blue-200 bg-blue-50/30" data-testid="detail-note-controllo-section">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-blue-900">
                      <FileText className="w-4 h-4" />Note controllo
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <p className="text-sm whitespace-pre-wrap" data-testid="detail-note-controllo-text">{activeCheck.note_controllo}</p>
                  </CardContent>
                </Card>
              )}

              {/* Allegati globali */}
              {(activeCheck.attachment_ids || []).length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Paperclip className="w-4 h-4" />Allegati controllo ({activeCheck.attachment_ids.length})</CardTitle></CardHeader>
                  <CardContent className="p-4 space-y-1">
                    {activeCheck.attachment_ids.map(attId => {
                      const att = activeAttachments[attId];
                      if (!att) {
                        return <div key={attId} className="text-xs text-slate-400 italic" data-testid={`detail-attachment-missing-${attId}`}>Allegato {attId.slice(0, 8)} non disponibile</div>;
                      }
                      return (
                        <a
                          key={attId}
                          href={buildFileUrl(att.file_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800 hover:underline bg-slate-50 px-2 py-1 rounded"
                          data-testid={`detail-attachment-${attId}`}
                        >
                          <Paperclip className="w-3 h-3" />
                          <span className="flex-1 truncate">{att.file_name}</span>
                          <span className="text-slate-400">{att.category || ''} · {att.file_size ? `${(att.file_size / 1024).toFixed(1)} KB` : ''}</span>
                        </a>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {(activeCheck.row_results || []).length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Caratteristiche compilate ({activeCheck.row_results.length})</CardTitle></CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-left uppercase text-slate-500 border-b">
                        <th className="p-2">N°</th><th className="p-2">Fase</th><th className="p-2">Caratteristica</th><th className="p-2">Tipo</th>
                        <th className="p-2">Nom/Tol</th><th className="p-2">UM</th><th className="p-2">Metodo</th>
                        <th className="p-2">Risultato</th><th className="p-2">Esito</th><th className="p-2">Strumento</th><th className="p-2">All.</th>
                      </tr></thead>
                      <tbody>
                        {activeCheck.row_results.map(r => (
                          <tr key={r.id} className={`border-b ${r.esito_riga === 'Non conforme' ? 'bg-red-50' : r.esito_riga === 'Conforme con note' ? 'bg-amber-50' : ''}`}>
                            <td className="p-2 font-mono">{r.numero_caratteristica}{r.obbligatorio && <span className="text-red-600 ml-1">*</span>}</td>
                            <td className="p-2">{r.fase_processo}</td>
                            <td className="p-2 max-w-[200px] truncate">{r.descrizione_caratteristica}</td>
                            <td className="p-2">{r.tipo_caratteristica}</td>
                            <td className="p-2 font-mono">{r.specifica_nominale || '—'} ({r.tolleranza_min || '—'}/{r.tolleranza_max || '—'})</td>
                            <td className="p-2">{r.unita_misura || '—'}</td>
                            <td className="p-2">{r.metodo_controllo_previsto}</td>
                            <td className="p-2 font-mono font-bold">{r.risultato_misurato || '—'}</td>
                            <td className="p-2"><Badge className={`${ESITO_BADGE[r.esito_riga] || ''} border text-[10px]`}>{r.esito_riga}</Badge></td>
                            <td className="p-2">{r.strumento_metodo_utilizzato || '—'}</td>
                            <td className="p-2">
                              {(r.attachment_ids || []).length === 0 ? (
                                <span className="text-slate-400">—</span>
                              ) : (
                                <div className="flex flex-col gap-0.5">
                                  {(r.attachment_ids || []).map(attId => {
                                    const att = activeAttachments[attId];
                                    if (!att) {
                                      return <span key={attId} className="text-[10px] text-slate-400 italic" data-testid={`row-attachment-missing-${attId}`}>n/d</span>;
                                    }
                                    return (
                                      <a
                                        key={attId}
                                        href={buildFileUrl(att.file_url)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[10px] text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1 max-w-[140px]"
                                        title={att.file_name}
                                        data-testid={`row-attachment-${attId}`}
                                      >
                                        <Paperclip className="w-2.5 h-2.5 flex-shrink-0" />
                                        <span className="truncate">{att.file_name}</span>
                                      </a>
                                    );
                                  })}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setDetailOpen(false)}>Chiudi</Button>
                <Button variant="outline" onClick={() => handleDownloadSinglePdf(activeCheck.id)} data-testid="detail-pdf-btn">
                  <Printer className="w-3 h-3 mr-1" />Scarica PDF
                </Button>
                {hasRole('admin', 'quality', 'operator') && (
                  <Button variant="outline" onClick={() => openCreateRqp(activeCheck)} className="border-orange-400 text-orange-700 hover:bg-orange-50" data-testid="create-rqp-btn">
                    <Wrench className="w-3 h-3 mr-1" />Crea richiesta produzione
                  </Button>
                )}
                {hasRole('admin', 'quality') && (
                  <Button onClick={() => openEditCheck(activeCheck)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="edit-check-btn">
                    <Pencil className="w-3 h-3 mr-1" />Modifica
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* RQP creation dialog (from check) */}
      <Dialog open={rqpOpen} onOpenChange={setRqpOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wrench className="w-5 h-5" />Crea Richiesta Qualità a Produzione</DialogTitle>
            <DialogDescription>Genera un foglio operativo per la produzione partendo da questo controllo. Cliente, prodotto, lotto, fase e dati caratteristica sono ereditati automaticamente.</DialogDescription>
          </DialogHeader>
          {rqpEnums && (
            <form onSubmit={handleRqpSubmit} className="space-y-3">
              {rqpRowOptions.length > 0 && (
                <div>
                  <Label>Riga misura collegata (opzionale)</Label>
                  <Select value={rqpForm.row_result_id || '__none'} onValueChange={v => setRqpForm({ ...rqpForm, row_result_id: v === '__none' ? '' : v })}>
                    <SelectTrigger data-testid="rqp-row-select"><SelectValue placeholder="Solo controllo (no riga specifica)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Solo controllo (no riga specifica)</SelectItem>
                      {rqpRowOptions.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500 mt-1">Selezionare una riga eredita anche numero caratteristica, tolleranze e valore misurato.</p>
                </div>
              )}
              <div>
                <Label>Motivo richiesta *</Label>
                <Textarea rows={2} value={rqpForm.motivo_richiesta} onChange={e => setRqpForm({ ...rqpForm, motivo_richiesta: e.target.value })} required placeholder="Es. Quota fuori tolleranza, da riprendere" data-testid="rqp-motivo" />
              </div>
              <div>
                <Label>Attività richiesta *</Label>
                <Textarea rows={2} value={rqpForm.attivita_richiesta} onChange={e => setRqpForm({ ...rqpForm, attivita_richiesta: e.target.value })} required placeholder="Es. Ripassare con rettifica fine il diametro Ø37,45" data-testid="rqp-attivita" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Quantità</Label>
                  <Input type="number" value={rqpForm.quantita_interessata} onChange={e => setRqpForm({ ...rqpForm, quantita_interessata: e.target.value })} placeholder="N. pezzi" />
                </div>
                <div>
                  <Label>Priorità *</Label>
                  <Select value={rqpForm.priorita} onValueChange={v => setRqpForm({ ...rqpForm, priorita: v })}>
                    <SelectTrigger data-testid="rqp-priorita"><SelectValue /></SelectTrigger>
                    <SelectContent>{rqpEnums.priorita.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Scadenza</Label>
                  <Input type="date" value={rqpForm.scadenza_richiesta} onChange={e => setRqpForm({ ...rqpForm, scadenza_richiesta: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Impatto operativo *</Label>
                <Select value={rqpForm.impatto_operativo} onValueChange={v => setRqpForm({ ...rqpForm, impatto_operativo: v })}>
                  <SelectTrigger data-testid="rqp-impatto"><SelectValue /></SelectTrigger>
                  <SelectContent>{rqpEnums.impatto_operativo.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={rqpForm.richiede_ricontrollo_qualita} onCheckedChange={v => setRqpForm({ ...rqpForm, richiede_ricontrollo_qualita: !!v })} />
                Richiede ricontrollo qualità dopo intervento
              </label>
              <div>
                <Label>Note qualità</Label>
                <Textarea rows={2} value={rqpForm.note_qualita} onChange={e => setRqpForm({ ...rqpForm, note_qualita: e.target.value })} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setRqpOpen(false)}>Annulla</Button>
                <Button type="submit" data-testid="rqp-submit">Crea richiesta</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
